import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from './LoadingScreen';
import useAdminPermissions from '../hooks/useAdminPermissions';

const ProtectedRoute = ({ children, role, requireVerifiedVendor = false }) => {
  const { user, isAdmin, isVendor, isCustomer, isAgent, loading, profile } = useAuth();

  // Admin routes sit behind two waits: the session, then the permission row.
  // PermissionGate used to own the second one, which meant two gates mounting
  // back to back — and because each mount restarts the animation from frame
  // zero, one load looked like two even once both drew the same screen.
  //
  // Waiting for both here collapses them into a single uninterrupted gate.
  // PermissionGate keeps its own loading branch as a safety net, but it should
  // never be reached from a route that came through here.
  //
  // The hook is called unconditionally because hooks must be; only the gating
  // is conditional, so non-admin routes are never held up by it.
  const { loading: permissionsLoading } = useAdminPermissions();

  if (loading || (isAdmin && permissionsLoading)) return <LoadingScreen />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin) {
    if (role && role !== 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return children;
  }

  if (profile?.status === 'suspended' || profile?.status === 'blocked') {
    return <Navigate to="/login" replace />;
  }

  // An agent belongs on the agent portal and nowhere else. Anything asking for
  // a different role sends them back rather than showing customer pages.
  if (isAgent && role && role !== 'agent') {
    return <Navigate to="/agent" replace />;
  }

  if (role === 'agent' && !isAgent) {
    return <Navigate to="/login" replace />;
  }

  if (role === 'customer' && !isCustomer) {
    return isVendor ? <Navigate to="/vendor" replace /> : <Navigate to="/login" replace />;
  }

  if (role === 'vendor' && !isVendor) {
    return isCustomer ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
  }

  if (role === 'vendor' && profile?.vendor_status === 'suspended') {
    return <Navigate to="/login" replace />;
  }

  if (role === 'vendor' && requireVerifiedVendor) {
    const verified =
      profile?.is_vendor_verified === true &&
      profile?.vendor_status === 'verified';

    if (!verified) {
      return <Navigate to="/vendor/profile" replace />;
    }
  }

  if (role === 'customer' && isVendor) {
    return <Navigate to="/vendor" replace />;
  }

  return children;
};

export default ProtectedRoute;